import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_URL}/reports`,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery,
  tagTypes: ['PfReport', 'EsiReport', 'PtReport', 'PayrollSummary', 'EmployeeReports', 'EmployeesList', 'ReportsSummary'],
  endpoints: (builder) => ({
    // Employee Reports endpoints
    getEmployeesList: builder.query({
      query: () => '/employees-list',
      providesTags: ['EmployeesList'],
    }),

    getEmployeeReports: builder.query({
      query: ({ employeeId, month, year, reportType, format = 'json' }) => ({
        url: '/employee-reports',
        params: {
          employeeId,
          month,
          year,
          reportType,
          format
        }
      }),
      providesTags: ['EmployeeReports'],
    }),

    // Payroll Summary endpoints
    getPayrollSummary: builder.query({
      query: ({ month, year, department = 'all', format = 'json' }) => ({
        url: '/payroll-summary',
        params: {
          month,
          year,
          department,
          format
        }
      }),
      providesTags: ['PayrollSummary'],
    }),

    // PF Report endpoints
    getPfReport: builder.query({
      query: ({ month, year, financialYear, format = 'json' }) => ({
        url: '/pf',
        params: {
          month,
          year,
          financialYear,
          format
        }
      }),
      providesTags: ['PfReport'],
    }),
    
    downloadPfReport: builder.mutation({
      query: ({ month, year, financialYear }) => ({
        url: '/pf',
        params: {
          month,
          year,
          financialYear,
          format: 'csv'
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `pf-report-${month || 'annual'}-${year || financialYear}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
      }),
    }),

    // ESI Report endpoints
    getEsiReport: builder.query({
      query: ({ month, year, financialYear, format = 'json' }) => ({
        url: '/esi',
        params: {
          month,
          year,
          financialYear,
          format
        }
      }),
      providesTags: ['EsiReport'],
    }),
    
    downloadEsiReport: builder.mutation({
      query: ({ month, year, financialYear }) => ({
        url: '/esi',
        params: {
          month,
          year,
          financialYear,
          format: 'csv'
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `esi-report-${month || 'annual'}-${year || financialYear}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
      }),
    }),

    // PT Report endpoints
    getPtReport: builder.query({
      query: ({ month, year, financialYear, state, format = 'json' }) => ({
        url: '/pt',
        params: {
          month,
          year,
          financialYear,
          state,
          format
        }
      }),
      providesTags: ['PtReport'],
    }),
    
    downloadPtReport: builder.mutation({
      query: ({ month, year, financialYear, state }) => ({
        url: '/pt',
        params: {
          month,
          year,
          financialYear,
          state,
          format: 'csv'
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `pt-report-${state || 'all'}-${month || 'annual'}-${year || financialYear}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          return { success: true };
        },
      }),
    }),

    // Reports Summary
    getReportsSummary: builder.query({
      query: () => '/summary',
      providesTags: ['ReportsSummary'],
    }),
  }),
});

export const {
  useGetEmployeesListQuery,
  useGetEmployeeReportsQuery,
  useGetPayrollSummaryQuery,
  useGetPfReportQuery,
  useDownloadPfReportMutation,
  useGetEsiReportQuery,
  useDownloadEsiReportMutation,
  useGetPtReportQuery,
  useDownloadPtReportMutation,
  useGetReportsSummaryQuery,
} = reportsApi;